import { Box, Button, Flex, Heading, Image, Input, Select, Text, useMediaQuery, useToast } from "@chakra-ui/react";
import { useMutation, useQuery } from "react-query";
import { Editor } from '@tinymce/tinymce-react';
import { theme } from "../../../assets/style/theme";
import Section from "../../../Component/Layout/Section";
import PageTitle from "../../../Component/Page/PageTitle";
import { editBoardPost, getBoardInfo, getBoardPost, getBoardTemplate, getWPPost, getWPpostTypeInfo, uploadFile, writeBoardPost } from '../../../service';
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { boardLabels, defaultUserInfo, testUserInfo } from "../meta";
import { Spinner } from '@chakra-ui/react'


declare var __AUTH__: any;

export const inputStyle = {
  size: 'lg',
  border: `1px solid ${theme.colors.dgr[500]}`,
}

type Props = {
  params:any;
};

const BoardWrite = ({ params }: Props) => {
  const toast = useToast();  
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [userInfo, setUserInfo] = useState(defaultUserInfo);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [thumbnailName, setThumbnailName] = useState('');
  const [youtube, setYoutube] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const isEnglish = window.__LANG__ === 'en';

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setUserInfo(testUserInfo);
    } else {
      const authInfo = __AUTH__ ? __AUTH__ : defaultUserInfo;

      if (params.board_id === '24' && authInfo.role !== 'administrator') {
        alert('관리자만 글을 작성할 수 있습니다.');
        window.location.href = isEnglish ? '/en/' : '/';
        return;
      }

      if (authInfo.isLoggedIn === false) {
        window.location.href = isEnglish ? '/en/login' : '/login';
      }
      setUserInfo(authInfo);
    }
  }, [isEnglish, params.board_id]);

  const requestInfo = params.reqmode === 'wp' ? getWPpostTypeInfo : getBoardInfo;

  const { isLoading:isLoadingBoardInfo, error:boardInfoError, data:boardInfo } = useQuery('BoardInfo', () => requestInfo(params.board_id));

  const requestPost = params.reqmode === 'wp' ? getWPPost : getBoardPost;

  const { isLoading:isLoadingPost, error:postError, data:post } = useQuery('PostEdit', () => requestPost(params.post_id), {
    enabled: params.post_id !== undefined
  });

  const { isLoading:isLoadingTemplate, data:boardTemplate } = useQuery(['BoardTemplate', params.board_id], () => getBoardTemplate(params.board_id), {
    enabled: params.mode === 'write' && params.reqmode !== 'wp' && params.board_id !== undefined
  });

  useEffect(() => {
    if (params.mode === 'write' && !isLoadingTemplate) {
      setIsLoading(false);
      return;
    }

    if (params.mode !== 'edit' || post === undefined) {
      return;
    }

    setTitle(post.title);
    setCategory(post.category1);
    setThumbnailUrl(post.thumbnail_file);
    setThumbnailName(post.thumbnail_name);
    setIsLoading(false);
  }, [post, params.mode, isLoadingTemplate])

  const { mutate:mutatePost, isLoading:isLoadingMutatePost, error:postMutateError } = useMutation((data:any) => writeBoardPost(data), {
    onSuccess: (data) => {
      toast({
        title: boardLabels('writeSuccess', Number(params.board_id)),
        status: "success",
        duration: 3000,
        position: "top",
      });
      setTimeout(() => {
        window.location.href = isEnglish ? `/en/board/?board_id=${params.board_id}` : `/board/?board_id=${params.board_id}`;
      }, 500);
    }
  });

  const { mutate:mutateEdit, isLoading:isLoadingMutateEdit, error:editMutateError } = useMutation((data:any) => editBoardPost(data), {
    onSuccess: (data) => {
      toast({
        title: boardLabels('editSuccess', Number(params.board_id)),
        status: "success",
        duration: 3000,
        position: "top",
      });
      setTimeout(() => {
        window.location.href = isEnglish ? `/en/board/?mode=view&board_id=${params.board_id}&post_id=${params.post_id}` : `/board/?mode=view&board_id=${params.board_id}&post_id=${params.post_id}`;
      }, 500);
    }
  });

  const { mutate:mutateThumbnail, isLoading:isLoadingThumbnail, error: thumbnailError } = useMutation((data:any) => uploadFile(data), {
    onSuccess: (data) => {
      setThumbnailName(data.url.split('/').reverse()[0]);
      setThumbnailUrl(data.url);
      toast({
        title: boardLabels('imageSuccess', Number(params.board_id)),
        status: "success",
        duration: 3000,
        position: "top",
      })
    }
  });

  const { mutate:mutateFile, isLoading:isLoadingFile, error:fileError } = useMutation((data:any) => uploadFile(data), {
    onSuccess: (data) => {
      setFileName(data.url.split('/').reverse()[0]);
      setFileUrl(data.url);
      toast({
        title: boardLabels('attachSuccess', Number(params.board_id)),
        status: "success",
        duration: 3000,
        position: "top",
      })
    }
  });

  const editorRef = useRef(null as any);
  
  const onChangeTitle = (e:any) => {
    setTitle(e.target.value);
  }

  const onChangeCategory = (e:any) => {
    setCategory(e.target.value);
  }

  const onChangeThumbnail = (e:any) => {
    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    mutateThumbnail(formData);
  }

  const onChangeYoutube = (e:any) => {
    setYoutube(e.target.value);
  }

  const onChangeFile = (e:any) => {
    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    mutateFile(formData);    
  }

  const handleWrite = () => {
    const data:any = {
      board_id: params.board_id,
      title: title,
      content: editorRef.current.getContent(),
      member_uid: userInfo.user.ID,
      member_display: userInfo.user.display_name,
      date: dayjs().format('YYYYMMDDHHmmss'),
      update: dayjs().format('YYYYMMDDHHmmss'),
      category1: category,
      parent_uid: 0,
      view: 0,
      comment: 0,
      like: 0,
      unlike: 0,
      vote: 0,
      thumbnail_file: thumbnailUrl,
      thumbnail_name: thumbnailName,
      category2: '',
      secret: '',
      notice: '',
      status: '',
      password: '',
      search: '',
      uid: params.post_id ? params.post_id : 0,
      youtube: youtube,
      fileurl: fileUrl,
      filename: fileName,
    }

    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }

    if (params.mode === 'write') {
      mutatePost(formData); 
      return;
    }

    if (params.mode === 'edit') {
      mutateEdit(formData);
      return;
    }
  }

  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const getInitialValue = () => {
    if (params.mode === 'edit') return post?.content ? post.content.replaceAll('\\"', '"') : '';
    if (boardTemplate?.content) return boardTemplate.content;
    if (params.board_id === '18') {
      return `
        <h1><strong>2024 풀씨 11기 활동을 공유해주세요&nbsp;<img class="emoji" role="img" draggable="false" src="https://s.w.org/images/core/emoji/14.0.0/svg/1f642.svg" alt="🙂" width="25" height="25"></strong></h1>
<p>&nbsp;</p>
<p>○ <strong>6월 17~23일(일) 자정까지</strong>&nbsp;작성 바랍니다.<br>○ 분량은 자유이나, 보고 내용이 미흡한 경우 보완요청이 있습니다.<br>○ 위의 '보고서 파일'란은 결과보고서 용으로 비워놔주세요.<br>○ 게시 내용 중 콘텐츠에 대한 초상권, 저작권 등에 주의바랍니다.<br>○ 활동을 영상으로 남겨주신다면 ☆대환영★, 다른 팀들 글도 둘러보시고 응원해주는 센스b</p>
<p>&nbsp;</p>
<p><strong>1. 해결하고자 하는 난제와 아이디어, 그 아이디어를 실험해볼 활동내용<br></strong>한 단락 요약</p>
<p>&nbsp;</p>
<p><strong>2. 현재까지 활동 경과보고<br></strong>언제, 어디서, 무엇을, 어떻게 했는지, 현재까지의 활동 진행과정을 상세히 작성해주세요.<br>본격적인 활동이 7월부터 진행될 예정이라면 지금까지의 준비 과정을 소개해주세요.<br>*활동이 부진한 경우도 괜찮으니 진행상황을 가감없이 작성 바랍니다.</p>
<p>&nbsp;</p>
<p><strong>3. 앞으로의 활동 계획<br></strong>남은 기간동안의 활동 계획과 앞으로의 활동에 개선점, 주의할 점, 기대되는 점 등을 작성해주세요.<br><strong>공식 행사 일정</strong>이 정해졌다면 시간, 장소를 구체적으로 작성 바랍니다.</p>
<p>&nbsp;</p>
<p><strong>4. (필수) 활동 사진<br></strong>최소 5장 이상, 가급적 2MB 이상 사진으로 첨부 바랍니다.</p>
<p>&nbsp;</p>
<p>&nbsp; &nbsp;&nbsp;&nbsp;</p>
      `;
    }
    if (params.board_id === '30') {
      return `
        <p>○ There is no limit on the amount of content, but complements may be requested if the post content or submitted documents are deemed insufficient.<br>○ Please pay attention to the participants’ portrait rights, copyrights, etc. of photos and content when posting.<br>○ Press ‘SHIFT+Enter’ for a single line down, and press ‘Enter’ for a paragraph down.Share&nbsp;the&nbsp;progress&nbsp;status&nbsp;of&nbsp;your&nbsp;project following the questions.</p>
<p>&nbsp;</p>
<p>1. Project description<br>Problems to be solved, Project purpose, and Activities you plan to do.<br>*Brief summary within a Paragraph</p>
<p>&nbsp;</p>
<p>2. Project progress up to now</p>
<p>Describe the details you did related your project including the preparation process so far.<br>*Details about Who, What, When, Where, Why and How</p>
<p>&nbsp;</p>
<p>3. Project plan forward</p>
<p>Describe your plan for the time remaining. Any improvements, cautions, and expectations for future activities.</p>
<p>&nbsp;</p>
<p>4. Photos<br>At least ten photos of project activity.<br>※ Photos: 2~10MB each / jpg, jpeg, png files</p>
      `;
  }
  return '';
}


  return (
    <>      
      {
        !isLoadingBoardInfo && !boardInfoError && (
          <PageTitle
            type="big"
            bgPath="board/news.jpg"
            title={boardInfo.parent_title}
            description={boardInfo.description}
          />
        )
      }
      {
        isLoading && (
          <Box textAlign="center" pb="100" pt="100">
            <Spinner size='xl' color='b.gr' />
          </Box>
        )
      }
      {
        !isLoadingBoardInfo && !boardInfoError && !isLoading && (
          <Section pt={40} pb={40} visible={!isLoading}>
            <Heading size={isMobile ? '3xl' : '5xl'} color="b.dgr" mb={10}>
              {boardInfo.board_name.replace(String.fromCharCode(92), '')}
            </Heading>
            <Box >
              <Input 
                {...inputStyle} 
                type='text' 
                value={title}
                onChange={onChangeTitle}
                placeholder={boardLabels('title', Number(params.board_id))}
              />
            </Box>
            {
              boardInfo.category1_list.length !== 0 && (
                <Box pt={8}>
                  <Flex alignItems="center">
                    <Text w="100px">
                      {boardLabels('category', Number(params.board_id))} 
                    </Text>
                    <Select size="lg" placeholder={boardLabels('selectCategory', Number(params.board_id))}  w="30%" onChange={onChangeCategory} value={category}>
                      {
                        boardInfo.category1_list.split(',').map((item:any) => (
                          <option key={item} value={item}>{item}</option>
                        ))
                      }
                    </Select>
                  </Flex>
                </Box>
              )
            }
            <Box pt={8}>
              <Flex alignItems="center">
                <Text w="100px">
                  {boardLabels('thumbnail', Number(params.board_id))} 
                </Text>
                {
                  thumbnailUrl.length !== 0 && (
                    <>
                      <Image src={thumbnailUrl} w="100px" h="auto" />
                      <Button size="sm" ml={4} onClick={() => {setThumbnailUrl(''); setThumbnailName('')}}>{boardLabels('removeThumbnail', Number(params.board_id))} </Button>
                    </>
                  )
                }
                {
                  thumbnailUrl.length === 0 && (
                    <Input {...inputStyle} type="file" w="30%" onChange={onChangeThumbnail} accept="image/*" />
                  )
                }
              </Flex>
            </Box>
            <Box pt={8}>
              <Flex alignItems="center">
                <Text w="100px">
                  {boardLabels('attach', Number(params.board_id))}
                </Text>
                <Input {...inputStyle} type="file" w="30%" onChange={onChangeFile} />
              </Flex>
            </Box>
            <Box pt={8}>
              <Flex alignItems="center">
                <Text w="100px">
                 {boardLabels('youtubeLink', Number(params.board_id))}
                </Text>
                <Input {...inputStyle} type="text" w="30%" onChange={onChangeYoutube} />
              </Flex>
            </Box>
            <Box pt={8}>
              <Editor
                apiKey='nend19nsesndnvg8e6074md4itmf3dtwp3g3cs5rp8dlpzty'
                onInit={(evt, editor) => {
                  editorRef.current = editor;
                }}
                initialValue={getInitialValue()}
                init={{
                  height: 800,
                  menubar: false,
                  // language: isEnglish ? 'en_US' : 'ko_KR',
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | image | help',
                  content_style: 'body { font-family:NanumSquareRound,sans-serif; font-size:16px }',
                  images_upload_url: 'http://koreashe.org/api/?mode=upload_file',
                  file_picker_types: 'file image media',
                  relative_urls : false,
                  remove_script_host : false,
                  convert_urls : true,
                }}
              />
            </Box>
            <Box pt={8} textAlign="right">
              <Button colorScheme="dgr" size="md" onClick={handleWrite} disabled={isLoadingMutatePost || isLoading}>
                {
                  params.mode === 'edit' ? boardLabels('edit', Number(params.board_id)) : boardLabels('write', Number(params.board_id))
                }
              </Button>
            </Box>
        </Section>
        )
      }
      
    </>
  );
};

export default BoardWrite;
